import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import {
  Upload, Cloud, Link as LinkIcon, Trash2, RefreshCw, Copy, Clock,
  CheckCircle2, AlertCircle, Plug, X, Shield, Search, Send, Mail, ChevronDown, ChevronUp, Pause, Play, RotateCw, UserRound
} from 'lucide-react';
import { toast } from 'sonner';
import EventModePanel from '../components/EventModePanel';
import FaceSearchTransferButton from '../components/FaceSearchTransferButton';


const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${units[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const daysRemaining = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso) - Date.now();
  if (ms < 0) return 'expired';
  const d = Math.ceil(ms / (1000 * 60 * 60 * 24));
  return `${d} day${d === 1 ? '' : 's'} left`;
};

const EXPIRY_OPTIONS = [1, 3, 7, 15, 30];
const ACCOUNT_KEYS = [
  { key: 'local', label: 'Local (built-in)' },
  { key: 'witty_shadows', label: 'Witty Shadows' },
  { key: 'trio_bliss', label: 'Trio Bliss' },
];

// Speed presets control how many files upload in parallel. Chunk size is fixed
// at 16 MB inside uploadOne (bigger chunks = fewer round-trips; already tuned
// for typical office/venue Wi-Fi). Higher concurrency is faster but exposes the
// upload to Google's occasional aggressive throttling on flaky networks.
const SPEED_PRESETS = {
  slow:     { concurrency: 1, label: 'Slow',     hint: 'One file at a time · safest on flaky Wi-Fi' },
  balanced: { concurrency: 3, label: 'Balanced', hint: '3 files in parallel · recommended default' },
  fast:     { concurrency: 5, label: 'Fast',     hint: '5 files in parallel · needs stable, fast Wi-Fi' },
};
const SPEED_PRESET_KEY = 'wittyshadows:transfers:speed';
const getStoredSpeedPreset = () => {
  try {
    const v = localStorage.getItem(SPEED_PRESET_KEY);
    return SPEED_PRESETS[v] ? v : 'balanced';
  } catch { return 'balanced'; }
};

// ---- Folder support ---------------------------------------------------------------------------
// Folders (dropped or picked) are read completely, sub-folders included, and flattened into the
// transfer — the Drive folder stays flat, exactly like today. The path inside the folder is kept for
// display only. System files are skipped, and duplicate names (two cards with DSC_0001.jpg) get
// " (2)", " (3)"… so every row and its upload status stay unique.
const JUNK_SEGMENT = /^(\.|~\$|__MACOSX$|Thumbs\.db$|desktop\.ini$)/i;
const isJunkPath = (path) => String(path || '').split('/').some((seg) => JUNK_SEGMENT.test(seg));
const withDir = (file, relPath) => {
  const dir = String(relPath || '').split('/').slice(0, -1).join('/');
  if (dir) {
    try { Object.defineProperty(file, '_dir', { value: dir, writable: true, configurable: true }); } catch (_) { /* ignore */ }
  }
  return file;
};
const renameFile = (file, name) => {
  try {
    const copy = new File([file], name, { type: file.type, lastModified: file.lastModified });
    return withDir(copy, file._dir ? `${file._dir}/${name}` : '');
  } catch (_) { return file; }
};
const uniqueName = (name, taken) => {
  if (!taken.has(name)) return name;
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  for (let i = 2; i < 10000; i++) {
    const candidate = `${base} (${i})${ext}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Date.now()}${ext}`;
};
// Reads a dropped FileSystemEntry tree (files + folders, any depth) into File objects.
const readEntryTree = (entry, prefix = '') => new Promise((resolve) => {
  if (!entry) return resolve([]);
  if (entry.isFile) {
    entry.file((file) => resolve([withDir(file, prefix + entry.name)]), () => resolve([]));
    return;
  }
  if (!entry.isDirectory) return resolve([]);
  const reader = entry.createReader();
  const collected = [];
  const step = () => reader.readEntries(async (batch) => {
    if (!batch.length) {
      const nested = await Promise.all(collected.map((e) => readEntryTree(e, `${prefix}${entry.name}/`)));
      resolve(nested.flat());
      return;
    }
    collected.push(...batch);
    step();
  }, () => resolve([]));
  step();
});
// Must be called synchronously inside the drop handler (entries only exist during the event).
const filesFromDataTransfer = (dt) => {
  const items = dt && dt.items ? Array.from(dt.items) : [];
  const entries = items
    .map((it) => (it.kind === 'file' && typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
    .filter(Boolean);
  if (entries.length === 0) return Promise.resolve(Array.from((dt && dt.files) || []));
  return Promise.all(entries.map((e) => readEntryTree(e))).then((lists) => lists.flat());
};

const TransfersPage = () => {
  const { user } = useAuth();
  const isAdmin = (user?.roles || [user?.role]).includes('admin');

  const [accounts, setAccounts] = useState([]);
  // Drive reconnect prompt — set when backend returns action=reconnect_drive.
  // Rendered as an inline banner + prominent Reconnect button.
  const [reconnectPrompt, setReconnectPrompt] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload state
  const [accountKey, setAccountKey] = useState('local');
  // Composer tab: 'transfer' (existing multi-file client delivery) or 'event' (Event Mode)
  const [composerMode, setComposerMode] = useState('transfer');
  // Speed preset (Slow/Balanced/Fast) — persisted per browser
  const [speedPreset, setSpeedPreset] = useState(getStoredSpeedPreset());
  const setSpeedPresetPersist = (v) => {
    setSpeedPreset(v);
    try { localStorage.setItem(SPEED_PRESET_KEY, v); } catch { /* ignore */ }
  };
  // Live throughput ({ bps, uploadedBytes, totalBytes, etaSeconds }). Updated
  // by uploadOne via bumpProgress; reset each batch.
  const throughputRef = useRef({ startedAt: null, uploadedBytes: 0, totalBytes: 0 });
  const [throughput, setThroughput] = useState({ bps: 0, uploadedBytes: 0, totalBytes: 0, etaSeconds: null });
  const [files, setFiles] = useState([]);
  const [expiryDays, setExpiryDays] = useState(7);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [recipientEmails, setRecipientEmails] = useState('');
  const [uploading, setUploading] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [perFileProgress, setPerFileProgress] = useState({}); // {name: pct}
  // Per-file upload status: {name: 'pending'|'uploading'|'paused'|'error'|'done', error?, sessionUrl?}
  const [perFileStatus, setPerFileStatus] = useState({});
  // Finalize state: 'idle' | 'finalizing' | 'error' — separate from per-file so users see it distinctly
  const [finalizeState, setFinalizeState] = useState({ status: 'idle', error: null, retryCount: 0 });
  // Shared refs that live across renders — the actual upload orchestration reads these
  const perFileStatusRef = useRef({});
  const transferIdRef = useRef(null);
  const uploadedFilesRef = useRef([]);
  const [shareResult, setShareResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // "Add a day" — upload more files into an EXISTING transfer link (multi-day events). While `addTo` is
  // set, the composer uploads into that transfer (same share link, same Drive folder) instead of creating
  // a new one, and finishes with POST /transfers/:id/days/:day/complete instead of /transfers/finalize.
  const [addTo, setAddTo] = useState(null); // { transfer, day, notify }
  const [addDayDialog, setAddDayDialog] = useState(null); // { transfer, label, notify, busy }
  const [daysByTransfer, setDaysByTransfer] = useState({}); // transfer id → { days: [...], grouped }
  const composerRef = useRef(null);
  const folderInputRef = useRef(null);
  const [showAllFiles, setShowAllFiles] = useState(false);

  // Recent transfers list state
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [resendDialog, setResendDialog] = useState(null); // {id, defaultEmails}
  const [resendExtras, setResendExtras] = useState('');
  const [resendBusy, setResendBusy] = useState(false);

  useEffect(() => {
    fetchAll();
    // Refresh once if user just returned from OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected')) {
      toast.success(`${params.get('connected').replace('_', ' ')} drive connected`);
      setReconnectPrompt(null);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('drive_error')) {
      toast.error(params.get('drive_error'), { duration: 10000 });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([
        api.get('/transfers/accounts'),
        api.get('/transfers', { params: searchTerm ? { search: searchTerm } : {} }),
      ]);
      setAccounts(a.data.accounts || []);
      setTransfers(t.data || []);
      // Default: prefer Drive if connected, else local
      const connectedDrive = (a.data.accounts || []).find(x => x.connected && !x.is_local);
      if (connectedDrive) setAccountKey(connectedDrive.account_key);
      else setAccountKey('local');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch transfers list when search term changes (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/transfers', { params: searchTerm ? { search: searchTerm } : {} });
        setTransfers(res.data || []);
      } catch (_) { /* silent — search failures don't need UI toast */ }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const connect = async (key) => {
    try {
      const res = await api.get(`/transfers/connect/${key}`);
      window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const disconnect = async (key) => {
    if (!window.confirm(`Disconnect ${key.replace('_', ' ')}? Existing transfers will continue working until expiry.`)) return;
    try {
      await api.delete(`/transfers/accounts/${key}`);
      toast.success('Disconnected');
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleFiles = (selected) => {
    const raw = Array.from(selected || []);
    const incoming = raw
      .map((f) => (f && f.webkitRelativePath && !f._dir ? withDir(f, f.webkitRelativePath) : f))
      .filter((f) => f && typeof f.size === 'number' && !isJunkPath(f._dir ? `${f._dir}/${f.name}` : f.name));
    const skipped = raw.length - incoming.length;
    if (incoming.length === 0) {
      if (skipped > 0) toast.info('Only system files were found — nothing added');
      return;
    }
    setFiles(prev => {
      const taken = new Set(prev.map((f) => f.name));
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}|${f.lastModified}`));
      const out = [...prev];
      for (const f of incoming) {
        const sig = `${f.name}|${f.size}|${f.lastModified}`;
        if (seen.has(sig)) continue; // same file added twice
        seen.add(sig);
        const name = uniqueName(f.name, taken);
        taken.add(name);
        out.push(name === f.name ? f : renameFile(f, name));
      }
      return out;
    });
    if (incoming.some((f) => f._dir) || incoming.length > 20) {
      toast.success(`Added ${incoming.length} file${incoming.length === 1 ? '' : 's'}${skipped > 0 ? ` · ${skipped} system file${skipped === 1 ? '' : 's'} skipped` : ''}`);
    }
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (!e.dataTransfer) return;
    filesFromDataTransfer(e.dataTransfer).then(handleFiles); // folders are read recursively
  };

  // Registry of per-file upload controllers, so pause/resume/retry buttons can
  // interact with in-flight uploads. Keyed by file index (files[i]).
  const uploadCtrlsRef = useRef({});

  // Direct browser → Google resumable upload with chunking, retry, resume & pause.
  //
  // Strict adherence to Google's resumable upload protocol:
  //   1) On ANY chunk failure (network / timeout / 5xx), the FIRST action is a
  //      status query: empty PUT with `Content-Range: bytes */TOTAL` against the
  //      SAME session URL. This tells us whether Google already committed the
  //      file (200/201) OR needs more bytes (308 with Range header) OR the
  //      session is truly gone (404/410).
  //   2) We NEVER create a new session on transient errors — that would upload
  //      the file again from byte zero, causing duplicates in Drive.
  //   3) The final chunk gets a longer timeout because Google's server-side
  //      commit can legitimately take 30-60s for large files, and premature
  //      abort was the root cause of "network error at 95%" duplicates.
  const uploadOne = async (file, uploadUrl, onProgress, onRetry, ctrl) => {
    // 16 MB chunks: 4× fewer round-trips than the old 4 MB size — a 50 MB
    // photo goes from 13→4 chunks, a 5 GB video goes from 1,250→313 chunks.
    // Google Drive's resumable upload API supports up to 256 MB per chunk; 16 MB
    // is a sweet spot for reliability (survives a chunk retry cheaply) and speed
    // on typical office/venue Wi-Fi (5-50 Mbps upload).
    const CHUNK_SIZE = 16 * 1024 * 1024;
    const MAX_RETRIES = 12;
    const MIN_BACKOFF_MS = 2000;
    // Timeouts: chunk PUT vs final-chunk commit vs status query
    const CHUNK_TIMEOUT_MS = 10 * 60 * 1000;       // 10 min for regular chunks (bigger chunks may take longer on slow Wi-Fi)
    const FINAL_CHUNK_TIMEOUT_MS = 20 * 60 * 1000; // 20 min for last chunk (Google commits file)
    const STATUS_QUERY_TIMEOUT_MS = 60 * 1000;     // 1 min for status queries

    ctrl.position = ctrl.position || 0;

    const putChunk = (start, end, isFinal) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      ctrl.activeXhr = xhr;
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Range', `bytes ${start}-${end - 1}/${file.size}`);
      xhr.timeout = isFinal ? FINAL_CHUNK_TIMEOUT_MS : CHUNK_TIMEOUT_MS;
      xhr.onload = () => {
        ctrl.activeXhr = null;
        if (xhr.status === 308) {
          const range = xhr.getResponseHeader('Range');
          const nextStart = range ? parseInt(range.split('-')[1], 10) + 1 : end;
          resolve({ done: false, nextStart });
        } else if (xhr.status === 200 || xhr.status === 201) {
          try { resolve({ done: true, meta: JSON.parse(xhr.responseText) }); }
          catch { reject({ transient: true, message: 'Drive returned invalid JSON' }); }
        } else if (xhr.status === 404 || xhr.status === 410) {
          reject({ transient: true, sessionGone: true, message: 'Session expired (rare)' });
        } else if (xhr.status >= 500 && xhr.status < 600) {
          reject({ transient: true, message: `Drive ${xhr.status}` });
        } else if (xhr.status === 408 || xhr.status === 429) {
          reject({ transient: true, message: `Drive ${xhr.status}` });
        } else if (xhr.status === 0) {
          reject({ transient: true, networkError: true, message: 'Network error' });
        } else {
          reject(new Error(`Drive ${xhr.status}: ${xhr.responseText}`));
        }
      };
      xhr.onabort = () => { ctrl.activeXhr = null; reject({ paused: true }); };
      xhr.onerror = () => { ctrl.activeXhr = null; reject({ transient: true, networkError: true, message: 'Network error' }); };
      xhr.ontimeout = () => { ctrl.activeXhr = null; reject({ transient: true, message: 'Timeout' }); };
      xhr.send(file.slice(start, end));
    });

    // Status query — the KEY primitive for the protocol. Empty PUT with
    // `bytes */TOTAL` tells us whether Google already has the file, where it
    // is in the upload, or whether the session is gone.
    // Returns: { status: 'done', meta } | { status: 'incomplete', position } | { status: 'session_gone' } | { status: 'unknown' }
    const queryStatus = () => new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      ctrl.activeXhr = xhr;
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Range', `bytes */${file.size}`);
      xhr.timeout = STATUS_QUERY_TIMEOUT_MS;
      xhr.onload = () => {
        ctrl.activeXhr = null;
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const meta = JSON.parse(xhr.responseText);
            resolve({ status: 'done', meta });
          } catch { resolve({ status: 'unknown' }); }
        } else if (xhr.status === 308) {
          const range = xhr.getResponseHeader('Range');
          // No Range header means Google has 0 bytes yet
          const position = range ? parseInt(range.split('-')[1], 10) + 1 : 0;
          resolve({ status: 'incomplete', position });
        } else if (xhr.status === 404 || xhr.status === 410) {
          resolve({ status: 'session_gone' });
        } else {
          resolve({ status: 'unknown' });
        }
      };
      xhr.onerror = () => { ctrl.activeXhr = null; resolve({ status: 'unknown' }); };
      xhr.onabort = () => { ctrl.activeXhr = null; resolve({ status: 'unknown' }); };
      xhr.ontimeout = () => { ctrl.activeXhr = null; resolve({ status: 'unknown' }); };
      xhr.send(new Blob([]));
    });

    let attempts = 0;
    onProgress(Math.round((ctrl.position / file.size) * 100), false);
    while (ctrl.position < file.size) {
      if (ctrl.pauseRequested || ctrl.cancelled) {
        throw { paused: !ctrl.cancelled, cancelled: ctrl.cancelled, position: ctrl.position };
      }
      const end = Math.min(ctrl.position + CHUNK_SIZE, file.size);
      const isFinal = end === file.size;
      try {
        const res = await putChunk(ctrl.position, end, isFinal);
        attempts = 0;
        if (res.done) {
          onProgress(100, false);
          if (onRetry) onRetry(null);
          return res.meta;
        }
        ctrl.position = res.nextStart || end;
        onProgress(Math.round((ctrl.position / file.size) * 100), false);
      } catch (err) {
        if (err?.paused) {
          throw { paused: !ctrl.cancelled, cancelled: ctrl.cancelled, position: ctrl.position };
        }
        attempts += 1;
        if (!err?.transient || attempts > MAX_RETRIES) {
          throw new Error(err?.message || 'Upload failed');
        }
        if (onRetry) onRetry({ attempt: attempts, max: MAX_RETRIES, reason: err.message });

        // PROTOCOL STEP: ALWAYS query the SAME session URI first to learn
        // Google's actual state, BEFORE any wait or retry. This is what prevents
        // duplicate uploads — if Google committed the file (common on final
        // chunk network timeouts), we mark it done immediately.
        const s = await queryStatus();
        if (s.status === 'done') {
          onProgress(100, false);
          if (onRetry) onRetry(null);
          return s.meta;
        }
        if (s.status === 'incomplete') {
          // Trust Google's position and continue from there
          ctrl.position = s.position;
          onProgress(Math.round((ctrl.position / file.size) * 100), true);
          // Small backoff before next chunk to let the network settle
          const backoff = MIN_BACKOFF_MS * Math.pow(1.5, attempts - 1) + Math.round(Math.random() * 500);
          await new Promise(r => setTimeout(r, backoff));
          continue;
        }
        if (s.status === 'session_gone') {
          // Only truly-gone sessions require a fresh session — 404/410 explicit.
          // Signal to the outer layer so it can decide whether to give up or
          // start a whole new upload for this file.
          throw new Error('Session expired (404/410) — please remove this file and re-add it');
        }
        // status === 'unknown' — Google didn't answer. Back off and re-loop;
        // the next iteration will try the same chunk (idempotent — Google
        // dedupes by Content-Range) or query again.
        const backoff = MIN_BACKOFF_MS * Math.pow(1.5, attempts - 1) + Math.round(Math.random() * 500);
        await new Promise(r => setTimeout(r, backoff));
      }
    }
    // If we exited the loop with position === file.size, do a final status
    // query — Google may have committed but our last PUT didn't return 200
    const final = await queryStatus();
    if (final.status === 'done') {
      onProgress(100, false);
      return final.meta;
    }
    throw new Error('Upload finished but Drive did not confirm the file — try again');
  };

  // Upload a file in chunks to our local backend (used when account_key === 'local')
  const uploadOneLocalChunked = async (file, uploadUrl, transferId, onProgress) => {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    const fileId = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `f_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    let lastJson = null;
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(file.size, start + CHUNK_SIZE);
      const chunk = file.slice(start, end);
      const fd = new FormData();
      fd.append('transfer_id', transferId);
      fd.append('file_name', file.name);
      fd.append('file_id', fileId);
      fd.append('chunk_index', String(i));
      fd.append('total_chunks', String(totalChunks));
      fd.append('file', chunk, file.name);
      // Use XMLHttpRequest to track progress per chunk
      lastJson = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', uploadUrl, true);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            const chunkPct = evt.loaded / evt.total;
            const overall = Math.round(((i + chunkPct) / totalChunks) * 100);
            onProgress(overall);
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error('Bad response')); }
          } else {
            reject(new Error(`Chunk upload error ${xhr.status}: ${xhr.responseText}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(fd);
      });
    }
    return lastJson; // {id, name, size, complete}
  };

  // Retry wrapper for the session-init POST — handles transient 502s from
  // the origin (e.g. Cloudflare) during bulk operations.
  const createSession = async (fileArg, transferIdArg, attempt = 0) => {
    try {
      const res = await api.post('/transfers/upload-session', {
        account_key: accountKey,
        transfer_id: transferIdArg,
        file_name: fileArg.name,
        file_size: fileArg.size,
        mime_type: fileArg.type || 'application/octet-stream',
      });
      return res.data;
    } catch (e) {
      const status = e?.response?.status;
      // 401 with structured `action: reconnect_drive` — Drive OAuth is dead.
      // Do NOT retry. Bubble up so the batch fails cleanly and the reconnect
      // banner shows up.
      const detail = e?.response?.data?.detail;
      if (status === 401 && detail && typeof detail === 'object' && detail.action === 'reconnect_drive') {
        setReconnectPrompt({ accountKey: detail.account_key, message: detail.message });
        const err = new Error(detail.message || 'Drive reconnect required');
        err.reconnect = true;
        throw err;
      }
      const transient = !status || (status >= 500 && status < 600);
      if (transient && attempt < 4) {
        const wait = 1000 * Math.pow(2, attempt) + Math.round(Math.random() * 500);
        await new Promise(r => setTimeout(r, wait));
        return createSession(fileArg, transferIdArg, attempt + 1);
      }
      throw e;
    }
  };

  const bumpProgress = (name, pct) => {
    setPerFileProgress(prev => {
      const next = { ...prev, [name]: pct };
      const totalBytes = files.reduce((a, f) => a + f.size, 0);
      const doneBytes = files.reduce((a, f) => a + f.size * ((next[f.name] || 0) / 100), 0);
      setOverallProgress(Math.round((doneBytes / Math.max(1, totalBytes)) * 100));

      // Throughput tracker — bps + ETA across the whole batch. Recomputed on
      // every progress tick using cumulative bytes uploaded / seconds elapsed.
      const startedAt = throughputRef.current.startedAt;
      if (startedAt) {
        const elapsedSec = Math.max(0.5, (Date.now() - startedAt) / 1000);
        const bps = doneBytes / elapsedSec;
        const remainingBytes = Math.max(0, totalBytes - doneBytes);
        const etaSeconds = bps > 0 ? Math.round(remainingBytes / bps) : null;
        setThroughput({ bps, uploadedBytes: doneBytes, totalBytes, etaSeconds });
      }
      return next;
    });
  };

  const setRetry = (name, retry) => {
    setFileStatus(name, { retry: retry });
  };

  const setFileStatus = (name, patch) => {
    setPerFileStatus(prev => {
      const next = { ...prev, [name]: { ...(prev[name] || {}), ...patch } };
      perFileStatusRef.current = next;
      return next;
    });
    perFileStatusRef.current = { ...perFileStatusRef.current, [name]: { ...(perFileStatusRef.current[name] || {}), ...patch } };
  };

  // Get a fresh Google resumable session URL for the given file — used when the
  // current session URL has become unresponsive. Called from within uploadOne.
  const refreshSessionUrl = async (fileArg /* position kept in ctrl */) => {
    const sess = await createSession(fileArg, transferIdRef.current);
    return sess.upload_url;
  };

  // Upload a single file — dispatches to the Drive resumable-upload protocol
  // (uploadOne) or the local chunked POST endpoint (uploadOneLocalChunked)
  // depending on the selected account_key. Pause/retry only apply to Drive;
  // Local backend is a straight sequential POST loop.
  const uploadFileWithCtrl = async (idx, reuseSessionUrl = null) => {
    const f = files[idx];
    const existing = uploadCtrlsRef.current[idx] || {};
    const ctrl = { ...existing, pauseRequested: false, cancelled: false, activeXhr: null };
    ctrl.position = existing.position || 0;
    uploadCtrlsRef.current[idx] = ctrl;

    const isLocal = accountKey === 'local' || existing.isLocal === true;
    let sessionUrl = reuseSessionUrl || existing.sessionUrl;
    try {
      if (!sessionUrl) {
        setFileStatus(f.name, { status: 'uploading', error: null });
        const sess = await createSession(f, transferIdRef.current);
        if (!transferIdRef.current) transferIdRef.current = sess.transfer_id;
        sessionUrl = sess.upload_url;
        ctrl.sessionUrl = sessionUrl;
        ctrl.isLocal = !!sess.is_local;
      } else {
        setFileStatus(f.name, { status: 'uploading', error: null });
      }

      if (isLocal || ctrl.isLocal) {
        // Local backend — plain POST-per-chunk to /api/transfers/upload-local-chunk.
        // No resumable protocol, no pause/retry (backend is authoritative on state).
        const result = await uploadOneLocalChunked(
          f, sessionUrl, transferIdRef.current,
          (pct) => bumpProgress(f.name, pct),
        );
        bumpProgress(f.name, 100);
        uploadedFilesRef.current[idx] = {
          drive_file_id: result?.id || result?.file_id || `local-${f.name}`,
          name: f.name,
          size: f.size,
          mime_type: f.type || 'application/octet-stream',
        };
        setFileStatus(f.name, { status: 'done', retry: null });
        return;
      }

      // Drive backend — resumable PUT protocol with per-chunk retry + status query.
      const meta = await uploadOne(
        f, sessionUrl,
        (pct) => bumpProgress(f.name, pct),
        (retryInfo) => setRetry(f.name, retryInfo),
        ctrl,
        refreshSessionUrl,
      );
      bumpProgress(f.name, 100);
      uploadedFilesRef.current[idx] = {
        drive_file_id: meta.id,
        name: f.name,
        size: f.size,
        mime_type: f.type || 'application/octet-stream',
      };
      // Commit this file to the transfer right away so the early-shared link
      // serves it immediately (best-effort — finalize will cover it anyway).
      try {
        await api.post('/transfers/file-complete', {
          transfer_id: transferIdRef.current,
          drive_file_id: meta.id,
          name: f.name,
          size: f.size,
          mime_type: f.type || 'application/octet-stream',
        });
      } catch { /* non-fatal */ }
      setFileStatus(f.name, { status: 'done', retry: null });
    } catch (err) {
      if (err?.paused) {
        setFileStatus(f.name, { status: 'paused' });
        return;
      }
      setFileStatus(f.name, { status: 'error', error: err?.message || 'Upload failed', retry: null });
    }
  };

  const retryAllFailed = async () => {
    const failedIndices = files
      .map((f, i) => ({ f, i }))
      .filter(({ f }) => perFileStatus[f.name]?.status === 'error')
      .map(({ i }) => i);
    if (failedIndices.length === 0) return;
    toast.info(`Retrying ${failedIndices.length} failed file${failedIndices.length === 1 ? '' : 's'}…`);
    // Fire them through the same concurrency-limited worker pool
    const queue = [...failedIndices];
    const worker = async () => {
      while (queue.length > 0) {
        const idx = queue.shift();
        const ctrl = uploadCtrlsRef.current[idx];
        await uploadFileWithCtrl(idx, ctrl?.sessionUrl || null);
      }
    };
    const workers = [];
    for (let w = 0; w < Math.min(2, failedIndices.length); w++) {
      workers.push(worker());
    }
    await Promise.all(workers);
    checkAndFinalize();
  };

  const pauseAll = () => {
    files.forEach((_, idx) => {
      const st = perFileStatus[files[idx].name]?.status;
      if (st === 'uploading') pauseFile(idx);
    });
  };

  const resumeAll = async () => {
    const paused = files
      .map((f, i) => ({ f, i }))
      .filter(({ f }) => perFileStatus[f.name]?.status === 'paused')
      .map(({ i }) => i);
    if (paused.length === 0) return;
    const queue = [...paused];
    const worker = async () => {
      while (queue.length > 0) {
        const idx = queue.shift();
        const ctrl = uploadCtrlsRef.current[idx];
        await uploadFileWithCtrl(idx, ctrl?.sessionUrl || null);
      }
    };
    const workers = [];
    for (let w = 0; w < Math.min(2, paused.length); w++) {
      workers.push(worker());
    }
    await Promise.all(workers);
    checkAndFinalize();
  };

  const pauseFile = (idx) => {
    const ctrl = uploadCtrlsRef.current[idx];
    if (!ctrl) return;
    ctrl.pauseRequested = true;
    if (ctrl.activeXhr) {
      try { ctrl.activeXhr.abort(); } catch { /* ignore */ }
    }
  };

  const resumeFile = async (idx) => {
    const f = files[idx];
    setFileStatus(f.name, { status: 'uploading' });
    // Reuse the same session URL — Google keeps sessions alive for ~7 days
    const ctrl = uploadCtrlsRef.current[idx];
    await uploadFileWithCtrl(idx, ctrl?.sessionUrl || null);
    checkAndFinalize();
  };

  const retryFile = async (idx) => {
    const f = files[idx];
    const ctrl = uploadCtrlsRef.current[idx];
    // On retry after an error, keep the session URL if we have one; otherwise reset.
    // Also try to resume from Google's known position by leaving ctrl.position alone.
    setFileStatus(f.name, { status: 'uploading', error: null });
    await uploadFileWithCtrl(idx, ctrl?.sessionUrl || null);
    checkAndFinalize();
  };

  // After any per-file completion, check whether all files are done — if so, finalize.
  // Finalize is retried with exponential backoff on transient failures. If it fails
  // permanently, the user gets a visible "Retry share link" button.
  const doFinalize = async (attempt = 0) => {
    const successful = uploadedFilesRef.current.filter(Boolean);
    if (successful.length === 0) return;
    if (addTo) return completeDay(successful, attempt);
    try {
      setFinalizeState({ status: 'finalizing', error: null, retryCount: attempt });
      const finalize = await api.post('/transfers/finalize', {
        transfer_id: transferIdRef.current,
        title: title || null,
        message: message || null,
        expiry_days: expiryDays,
        recipient_emails: recipientEmails
          ? recipientEmails.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        files: successful,
      });
      setShareResult(finalize.data);
      setOverallProgress(100);
      setFinalizeState({ status: 'idle', error: null, retryCount: 0 });
      toast.success('Upload complete!');
      setFiles([]);
      setTitle('');
      setMessage('');
      setRecipientEmails('');
      fetchAll();
      setUploading(false);
    } catch (e) {
      const status = e?.response?.status;
      const transient = !status || (status >= 500 && status < 600);
      if (transient && attempt < 5) {
        const wait = 1500 * Math.pow(2, attempt) + Math.round(Math.random() * 500);
        setFinalizeState({ status: 'finalizing', error: null, retryCount: attempt + 1 });
        await new Promise(r => setTimeout(r, wait));
        return doFinalize(attempt + 1);
      }
      // Give up — show visible error state (user can Retry finalize)
      const msg = e?.response ? formatApiError(e) : (e.message || 'Finalize failed');
      setFinalizeState({ status: 'error', error: msg, retryCount: attempt });
      toast.error(`Failed to publish transfer: ${msg}. Click "Retry" to try again.`);
      setUploading(true); // keep panel open so user can retry
    }
  };

  const checkAndFinalize = async () => {
    // Only skip if finalize has ALREADY completed (shareResult.preparing===false).
    // A `preparing: true` shareResult is the early-share panel — finalize still
    // needs to run when all files are uploaded.
    if (shareResult && !shareResult.preparing) return;
    const statuses = perFileStatusRef.current;
    const allDone = files.length > 0 && files.every(f => statuses[f.name]?.status === 'done');
    if (!allDone) return;
    await doFinalize(0);
  };

  const retryFinalize = () => {
    if (finalizeState.status !== 'error') return;
    doFinalize(0);
  };

  const startUpload = async () => {
    if (files.length === 0) return toast.error('Select at least one file');
    const acct = accounts.find(a => a.account_key === accountKey);
    if (accountKey !== 'local' && !acct?.connected) return toast.error('Selected drive is not connected. Ask an admin.');

    setUploading(true);
    setShareResult(null);
    setOverallProgress(0);
    setPerFileProgress({});
    setPerFileStatus({});
    perFileStatusRef.current = {};
    transferIdRef.current = null;
    uploadedFilesRef.current = new Array(files.length);
    uploadCtrlsRef.current = {};
    // Reset throughput tracker for this batch
    throughputRef.current = { startedAt: Date.now(), uploadedBytes: 0, totalBytes: files.reduce((a, f) => a + f.size, 0) };
    setThroughput({ bps: 0, uploadedBytes: 0, totalBytes: throughputRef.current.totalBytes, etaSeconds: null });

    // Seed pending status
    const initialStatus = Object.fromEntries(files.map(f => [f.name, { status: 'pending' }]));
    setPerFileStatus(initialStatus);
    perFileStatusRef.current = initialStatus;

    try {
      // Create the first upload session up front (serially) so we get a
      // transfer_id that all subsequent workers can pin to. Without this,
      // concurrent workers each see transferIdRef.current === null and
      // create three separate transfers.
      const firstIdx = 0;
      const firstFile = files[firstIdx];
      const firstCtrl = { pauseRequested: false, cancelled: false, position: 0 };
      uploadCtrlsRef.current[firstIdx] = firstCtrl;
      setFileStatus(firstFile.name, { status: 'uploading' });
      const firstSess = await createSession(firstFile, addTo ? addTo.transfer.id : null);
      transferIdRef.current = firstSess.transfer_id || (addTo ? addTo.transfer.id : null);
      // Store the session URL on file 0's ctrl so the worker below can reuse
      // it via uploadFileWithCtrl's `existing.sessionUrl` fallback — no
      // duplicate session gets created.
      firstCtrl.sessionUrl = firstSess.upload_url;
      firstCtrl.isLocal = !!firstSess.is_local;

      // Show the share link IMMEDIATELY — the moment the first session
      // (and therefore the transfer_id + share_token) exists on the backend.
      // Marked `preparing: true` so the UI shows a "still uploading" note next
      // to it. The link is fully live once checkAndFinalize runs after the last
      // file finishes; before that, opening it renders a "files being prepared"
      // screen instead of a 404.
      if (firstSess.share_link) {
        setShareResult({
          share_link: firstSess.share_link,
          share_token: firstSess.share_token,
          preparing: true,
        });
      }

      // Worker-pool concurrency comes from the user-facing speed preset stored
      // in localStorage (Slow=1 / Balanced=3 / Fast=5). Balanced is the default —
      // 3 parallel files is the sweet spot for stable Wi-Fi and Google Drive's
      // resumable-upload edge. Slow (1 file at a time) is the ultra-safe fallback
      // for venues with terrible Wi-Fi.
      //
      // A paused / errored file does NOT stall the queue — uploadFileWithCtrl
      // catches its own exceptions internally, sets the per-file status, and
      // returns normally, so the worker's next iteration pulls the next idx.
      const CONCURRENCY = SPEED_PRESETS[speedPreset]?.concurrency || 3;
      const queue = files.map((_, i) => i); // includes file 0
      const worker = async () => {
        while (queue.length > 0) {
          const idx = queue.shift();
          if (idx === undefined) break; // defensive; single-threaded JS shouldn't hit this
          await uploadFileWithCtrl(idx);
        }
      };
      const workerCount = Math.max(1, Math.min(CONCURRENCY, queue.length));
      const workers = [];
      for (let w = 0; w < workerCount; w++) {
        workers.push(worker());
      }
      await Promise.all(workers);
      await checkAndFinalize();
      // If some files are paused/errored, keep the panel open so the user can act.
      const stillPending = files.some(f => {
        const s = perFileStatusRef.current[f.name]?.status;
        return s === 'paused' || s === 'error' || s === 'uploading';
      });
      if (stillPending && !shareResult) setUploading(true);
    } catch (e) {
      toast.error(e?.response ? formatApiError(e) : (e.message || 'Upload failed'));
      setUploading(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied');
  };

  // Client gallery: persistent per-client URL that lists every active transfer
  // sent to that recipient email from this account. If a transfer has multiple
  // recipients, open a small dialog letting the admin choose which one's link
  // to copy. If only one recipient, copy immediately.
  const [galleryPicker, setGalleryPicker] = useState(null); // { transfer, emails }
  const copyGalleryFor = async (accountKey, email) => {
    try {
      const res = await api.get('/transfers/client-token', { params: { account_key: accountKey, email } });
      const url = res.data.url;
      await navigator.clipboard.writeText(url);
      toast.success(`Client gallery link copied for ${email}`);
      setGalleryPicker(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const copyClientGalleryLink = (t) => {
    const emails = t.recipient_emails || [];
    if (emails.length === 0) return;
    if (emails.length === 1) {
      copyGalleryFor(t.account_key, emails[0]);
    } else {
      setGalleryPicker({ transfer: t, emails });
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/transfers/${id}`);
      const d = res.data || {};
      if (d.is_local) {
        toast.success('Transfer deleted');
      } else if (d.files_failed > 0) {
        toast.warning(
          `Deleted ${d.files_deleted} file(s) from Drive · ${d.files_failed} could not be deleted (already gone or permission denied). Transfer removed from the list.`
        );
      } else {
        toast.success(
          `Deleted ${d.files_deleted} file(s) from Drive${d.folder_deleted ? ' + folder' : ''}`
        );
      }
      setDeleteDialog(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Delete confirmation modal state — replaces the native window.confirm so we
  // can show the exact number of files that will be permanently removed from
  // Google Drive before the user confirms.
  const [deleteDialog, setDeleteDialog] = useState(null); // transfer object or null
  const [deleteBusy, setDeleteBusy] = useState(false);

  const runCleanup = async () => {
    try {
      const res = await api.post('/transfers/admin/run-cleanup');
      toast.success(`Cleanup ran. Deleted ${res.data.deleted} expired transfers.`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Duplicate scan (dry-run) — admin-only. Reports duplicate files created by the
  // pre-fix retry bug. NEVER deletes anything.
  const [dupScan, setDupScan] = useState(null); // { loading, report, error }
  const runDuplicateScan = async () => {
    setDupScan({ loading: true, report: null, error: null });
    try {
      const res = await api.get('/transfers/admin/duplicate-scan');
      setDupScan({ loading: false, report: res.data, error: null });
    } catch (e) {
      setDupScan({ loading: false, report: null, error: formatApiError(e) });
    }
  };

  const openResend = (t) => {
    setResendDialog(t);
    setResendExtras('');
  };

  const submitResend = async () => {
    if (!resendDialog) return;
    const extras = resendExtras
      ? resendExtras.split(',').map(s => s.trim()).filter(Boolean)
      : [];
    setResendBusy(true);
    try {
      const res = await api.post(`/transfers/${resendDialog.id}/resend`, {
        recipient_emails: extras,
      });
      toast.success(`Resent to ${res.data.sent} recipient(s)`);
      setResendDialog(null);
      setResendExtras('');
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResendBusy(false);
    }
  };

  // ---- "Add a day" helpers ---------------------------------------------------------------------
  const loadDays = async (transferId) => {
    try {
      const res = await api.get(`/transfers/${transferId}/days`);
      setDaysByTransfer(prev => ({ ...prev, [transferId]: res.data }));
    } catch (_) { /* silent — the list simply stays hidden */ }
  };

  const openAddDay = (t) => {
    if (uploading) return toast.error('Wait for the current upload to finish first');
    loadDays(t.id);
    setAddDayDialog({ transfer: t, label: '', notify: false, busy: false });
  };

  const confirmAddDay = async () => {
    if (!addDayDialog || addDayDialog.busy) return;
    const { transfer, label, notify } = addDayDialog;
    setAddDayDialog(d => ({ ...d, busy: true }));
    try {
      const res = await api.post(`/transfers/${transfer.id}/days`, { label: label.trim() || null });
      setAddTo({ transfer, day: res.data, notify });
      setComposerMode('transfer');
      setAccountKey(transfer.account_key || 'local');
      setFiles([]);
      setShareResult(null);
      setFinalizeState({ status: 'idle', error: null, retryCount: 0 });
      setAddDayDialog(null);
      loadDays(transfer.id);
      toast.success(`${res.data.title} created — drop the files in and hit Upload`);
      setTimeout(() => composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (e) {
      toast.error(formatApiError(e));
      setAddDayDialog(d => (d ? { ...d, busy: false } : d));
    }
  };

  const cancelAddDay = async () => {
    if (!addTo) return;
    const { transfer, day } = addTo;
    setAddTo(null);
    setFiles([]);
    try { await api.delete(`/transfers/${transfer.id}/days/${day.id}`); } catch (_) { /* an empty day is harmless */ }
    loadDays(transfer.id);
  };

  const renameDay = async (t, d) => {
    const label = window.prompt(`Name for Day ${d.number} (optional — e.g. Haldi, Sangeet, Reception)`, d.label || '');
    if (label === null) return;
    try {
      await api.patch(`/transfers/${t.id}/days/${d.id}`, { label: label.trim() });
      loadDays(t.id);
      toast.success('Day renamed');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Replaces /transfers/finalize when the upload was an extra day: the transfer (title, expiry, link,
  // recipients) already exists — we only attach the new files to it and mark the day ready.
  const completeDay = async (successful, attempt = 0) => {
    const { transfer, day, notify } = addTo;
    try {
      setFinalizeState({ status: 'finalizing', error: null, retryCount: attempt });
      const res = await api.post(`/transfers/${transfer.id}/days/${day.id}/complete`, { files: successful });
      const d = res.data?.day || day;
      if (notify && (transfer.recipient_emails || []).length > 0) {
        try {
          await api.post(`/transfers/${transfer.id}/resend`, { recipient_emails: [] });
          toast.success('Recipients emailed about the new day');
        } catch (_) {
          toast.warning('Files were added, but the notification email could not be sent');
        }
      }
      setShareResult({
        share_link: transfer.share_link,
        share_token: transfer.share_token,
        expires_at: res.data?.transfer?.expires_at || transfer.expires_at,
        day_title: d.title,
        added: successful.length,
        face_sync: res.data?.face_sync,
      });
      setOverallProgress(100);
      setFinalizeState({ status: 'idle', error: null, retryCount: 0 });
      toast.success(`${d.title} added — ${successful.length} file${successful.length === 1 ? '' : 's'} now live on the same link`);
      setFiles([]);
      setAddTo(null);
      setDaysByTransfer(prev => { const next = { ...prev }; delete next[transfer.id]; return next; });
      fetchAll();
      setUploading(false);
    } catch (e) {
      const status = e?.response?.status;
      const transient = !status || (status >= 500 && status < 600);
      if (transient && attempt < 5) {
        const wait = 1500 * Math.pow(2, attempt) + Math.round(Math.random() * 500);
        setFinalizeState({ status: 'finalizing', error: null, retryCount: attempt + 1 });
        await new Promise(r => setTimeout(r, wait));
        return completeDay(successful, attempt + 1);
      }
      const msg = e?.response ? formatApiError(e) : (e.message || 'Could not complete the day');
      setFinalizeState({ status: 'error', error: msg, retryCount: attempt });
      toast.error(`Files uploaded, but the day could not be completed: ${msg}. Click "Retry" to try again.`);
      setUploading(true); // keep panel open so user can retry
    }
  };

  const selectedAccount = accounts.find(a => a.account_key === accountKey);
  const FILE_ROWS = 60;
  const visibleFiles = (showAllFiles || files.length <= FILE_ROWS) ? files : files.slice(0, FILE_ROWS);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6" data-testid="transfers-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Cloud className="w-8 h-8 text-[#F5A623]" />
              Transfers
            </h1>
            <p className="text-sm text-white/60 mt-1">Send big files via Google Drive. Auto-expire after the time you choose.</p>
          </div>
          <Button variant="outline" onClick={fetchAll} disabled={loading} data-testid="refresh-transfers">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Reconnect-required banner — shows when a Drive account's OAuth
            refresh_token has been revoked/expired. Backend surfaces this via
            the accounts list (needs_reconnect: true) OR via a session-create
            401 with action=reconnect_drive. Both feed into reconnectPrompt. */}
        {(reconnectPrompt || accounts.some(a => a.needs_reconnect)) && (() => {
          const stalled = reconnectPrompt
            ? accounts.find(a => a.account_key === reconnectPrompt.accountKey)
            : accounts.find(a => a.needs_reconnect);
          if (!stalled) return null;
          return (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="reconnect-banner">
              <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-300">
                  Drive reconnect required — {stalled.label}
                </div>
                <div className="text-xs text-amber-200/80 mt-1">
                  {reconnectPrompt?.message || `Google authorization for ${stalled.label} has expired. Uploads to this account will fail until you reconnect.`}
                </div>
                {isAdmin ? (
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" onClick={() => connect(stalled.account_key)}
                      className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="reconnect-btn">
                      <Plug className="w-3.5 h-3.5 mr-1.5" /> Reconnect {stalled.label}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setReconnectPrompt(null)}>Dismiss</Button>
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-amber-200/60">Ask an admin to reconnect this account.</div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Drive account status */}
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2"><Plug className="w-4 h-4" /> Drive Accounts</h2>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={runDuplicateScan} disabled={dupScan?.loading} data-testid="scan-duplicates-btn">
                    <Search className="w-4 h-4 mr-1" /> {dupScan?.loading ? 'Scanning…' : 'Scan for duplicates'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={runCleanup} data-testid="run-cleanup-btn">
                    <Shield className="w-4 h-4 mr-1" /> Run cleanup now
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {accounts.filter(a => !a.is_local).map((a) => (
                <div key={a.account_key}
                  className={`flex items-center justify-between p-4 rounded-lg border ${a.connected ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-white/10 bg-white/[0.02]'}`}
                  data-testid={`drive-account-${a.account_key}`}>
                  <div>
                    <div className="font-semibold flex items-center gap-2">
                      {a.label}
                      {a.connected
                        ? <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Connected</Badge>
                        : <Badge className="bg-white/5 text-white/60 border-white/10">Not connected</Badge>}
                    </div>
                    {a.connected_email && <div className="text-xs text-white/50 mt-1">{a.connected_email}</div>}
                  </div>
                  {isAdmin && (
                    a.connected
                      ? <Button variant="outline" size="sm" onClick={() => disconnect(a.account_key)} data-testid={`disconnect-${a.account_key}`}>Disconnect</Button>
                      : <Button size="sm" className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90" onClick={() => connect(a.account_key)} data-testid={`connect-${a.account_key}`}>Connect</Button>
                  )}
                </div>
              ))}
            </div>
            {!isAdmin && !accounts.some(a => a.connected && !a.is_local) && (
              <div className="mt-3 text-sm text-white/60 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Drive accounts not connected — uploads will use built-in local storage (50 GB max per file).
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload composer */}
        <div ref={composerRef}>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-5 space-y-4">
            {/* Composer mode tabs — "New transfer" is the default multi-file client
                delivery. "Event Mode" is the additive live-event flow: one photo →
                one QR / share link, so guests scan the code and get only their own
                photo. Event Mode is Witty Shadows only for now (sponsor). */}
            <div className="flex items-center gap-2 border-b border-white/10 -mx-5 px-5 pb-3">
              <button
                type="button"
                onClick={() => setComposerMode('transfer')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  composerMode === 'transfer'
                    ? 'bg-white/10 text-white'
                    : 'text-white/50 hover:text-white/80'
                }`}
                data-testid="composer-tab-transfer">
                New transfer
              </button>
              {(accounts.find(a => a.account_key === 'witty_shadows')?.connected) && (
                <button
                  type="button"
                  onClick={() => setComposerMode('event')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${
                    composerMode === 'event'
                      ? 'bg-[#F5A623]/10 text-[#F5A623] border border-[#F5A623]/30'
                      : 'text-white/50 hover:text-white/80'
                  }`}
                  data-testid="composer-tab-event">
                  Event Mode
                  <span className="text-[9px] uppercase tracking-wider bg-[#F5A623]/20 text-[#F5A623] px-1.5 py-0.5 rounded">
                    Witty Shadows
                  </span>
                </button>
              )}
            </div>

            {composerMode === 'event' ? (
              <EventModePanel accountKey="witty_shadows" />
            ) : (
              <>
            <h2 className="text-lg font-semibold">{addTo ? `Add ${addTo.day.title}` : 'New transfer'}</h2>

            {addTo && (
              <div className="rounded-lg border border-[#F5A623]/40 bg-[#F5A623]/5 p-3 flex flex-wrap items-center gap-3" data-testid="add-day-banner">
                <div className="flex-1 min-w-[200px] text-sm">
                  <div className="font-medium text-[#F5A623]">
                    Adding {addTo.day.title} to “{addTo.transfer.title || addTo.transfer.drive_folder_name || 'this transfer'}”
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">
                    Same share link{addTo.transfer.account_key !== 'local' ? ' and Drive folder' : ''} — clients see the files grouped by day
                    {addTo.notify ? ' · recipients will be emailed when the day is up' : ''}.
                  </div>
                </div>
                {!uploading && (
                  <Button size="sm" variant="ghost" onClick={cancelAddDay} data-testid="cancel-add-day">
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                )}
              </div>
            )}

            {/* Account toggle */}
            {!addTo && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {ACCOUNT_KEYS.map(({ key, label }) => {
                const acct = accounts.find(x => x.account_key === key);
                const isLocal = key === 'local';
                const enabled = isLocal || !!acct?.connected;
                const selected = accountKey === key;
                const sublabel = isLocal
                  ? 'Built-in · works now'
                  : (enabled ? 'Drive connected' : 'Not connected');
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!enabled || uploading}
                    onClick={() => setAccountKey(key)}
                    data-testid={`select-account-${key}`}
                    className={`px-4 py-3 rounded-lg border transition text-left
                      ${selected ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/10 bg-white/[0.02]'}
                      ${!enabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-white/30'}`}>
                    <div className="font-medium">{label}</div>
                    <div className="text-xs text-white/50 mt-1">{sublabel}</div>
                  </button>
                );
              })}
            </div>
            )}

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition
                ${dragActive ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-white/15 hover:border-white/30'}`}
              data-testid="dropzone">
              <Upload className="w-10 h-10 mx-auto text-white/40 mb-3" />
              <div className="font-medium">Drop files or folders here, or click to browse</div>
              <div className="text-xs text-white/50 mt-1">{accountKey === 'local' ? 'Up to 50 GB per file (built-in storage). Multiple files allowed.' : 'No size limit on Drive. Multiple files allowed.'}</div>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs">
                <button type="button" disabled={uploading}
                  onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
                  className="px-3 py-1.5 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 disabled:opacity-50"
                  data-testid="choose-folder-btn">
                  Choose a folder
                </button>
                <span className="text-white/40">Sub-folders are included · tip: one folder per day</span>
              </div>
              <input ref={fileInputRef} type="file" multiple className="hidden"
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="file-input" />
              <input ref={folderInputRef} type="file" webkitdirectory="" directory="" multiple className="hidden"
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} data-testid="folder-input" />
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className="space-y-2" data-testid="file-list">
                {/* Bulk action bar — visible during/after upload */}
                {uploading && (() => {
                  const counts = { uploading: 0, paused: 0, error: 0, done: 0, pending: 0 };
                  files.forEach(f => {
                    const s = perFileStatus[f.name]?.status || 'pending';
                    counts[s] = (counts[s] || 0) + 1;
                  });
                  return (
                    <div className="flex flex-wrap items-center gap-2 p-2 bg-white/[0.03] border border-white/10 rounded-lg">
                      <div className="flex-1 flex flex-wrap gap-3 text-xs text-white/60">
                        {counts.done > 0 && <span className="text-emerald-400">{counts.done} done</span>}
                        {counts.uploading > 0 && <span className="text-[#F5A623]">{counts.uploading} uploading</span>}
                        {counts.paused > 0 && <span className="text-amber-400">{counts.paused} paused</span>}
                        {counts.error > 0 && <span className="text-rose-400">{counts.error} failed</span>}
                        {counts.pending > 0 && <span>{counts.pending} queued</span>}
                      </div>
                      {counts.uploading > 0 && (
                        <button onClick={pauseAll} className="text-xs px-2 py-1 rounded border border-white/10 text-white/70 hover:bg-white/10" data-testid="pause-all">
                          <Pause className="w-3 h-3 inline mr-1" /> Pause all
                        </button>
                      )}
                      {counts.paused > 0 && (
                        <button onClick={resumeAll} className="text-xs px-2 py-1 rounded border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" data-testid="resume-all">
                          <Play className="w-3 h-3 inline mr-1" /> Resume all
                        </button>
                      )}
                      {counts.error > 0 && (
                        <button onClick={retryAllFailed} className="text-xs px-2 py-1 rounded border border-amber-500/30 text-amber-400 hover:bg-amber-500/10" data-testid="retry-all-failed">
                          <RotateCw className="w-3 h-3 inline mr-1" /> Retry all failed ({counts.error})
                        </button>
                      )}
                    </div>
                  );
                })()}
                {visibleFiles.map((f, idx) => {
                  const st = perFileStatus[f.name]?.status;
                  const err = perFileStatus[f.name]?.error;
                  const retry = perFileStatus[f.name]?.retry;
                  const pct = perFileProgress[f.name] ?? 0;
                  const badge =
                    st === 'done' ? { text: 'Done', cls: 'text-emerald-400' } :
                    st === 'paused' ? { text: 'Paused', cls: 'text-amber-400' } :
                    st === 'error' ? { text: 'Failed', cls: 'text-rose-400' } :
                    st === 'uploading' && retry ? { text: `Retrying ${retry.attempt}/${retry.max}`, cls: 'text-amber-400' } :
                    st === 'uploading' ? { text: `${pct}%`, cls: 'text-[#F5A623]' } :
                    uploading ? { text: 'Queued', cls: 'text-white/50' } : null;
                  return (
                    <div key={`${f.name}-${f.size}-${f.lastModified || ''}`} className="flex items-center justify-between text-sm bg-white/5 rounded px-3 py-2" data-testid={`file-row-${idx}`}>
                      <div className="truncate flex-1 min-w-0">
                        <div className="truncate">{f._dir ? <span className="text-white/30">{f._dir}/</span> : null}{f.name} <span className="text-white/40">· {formatSize(f.size)}</span></div>
                        {st === 'uploading' && (
                          <div className="mt-1 h-1 bg-white/10 rounded overflow-hidden">
                            <div className={`h-full ${retry ? 'bg-amber-400 animate-pulse' : 'bg-[#F5A623]'} transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                        )}
                        {st === 'uploading' && retry && (
                          <div className="text-[10px] text-amber-400/80 mt-0.5">
                            Network retry {retry.attempt}/{retry.max} · {retry.reason}
                          </div>
                        )}
                        {st === 'error' && err && (
                          <div className="text-xs text-rose-400/80 mt-0.5 truncate" title={err}>{err}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-3">
                        {badge && <span className={`text-xs font-medium ${badge.cls} min-w-[42px] text-right`}>{badge.text}</span>}
                        {st === 'uploading' && (
                          <button onClick={() => pauseFile(idx)} title="Pause" className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10" data-testid={`pause-${idx}`}>
                            <Pause className="w-4 h-4" />
                          </button>
                        )}
                        {st === 'paused' && (
                          <button onClick={() => resumeFile(idx)} title="Resume" className="text-emerald-400 hover:text-emerald-300 p-1 rounded hover:bg-white/10" data-testid={`resume-${idx}`}>
                            <Play className="w-4 h-4" />
                          </button>
                        )}
                        {st === 'error' && (
                          <button onClick={() => retryFile(idx)} title="Retry" className="text-amber-400 hover:text-amber-300 p-1 rounded hover:bg-white/10" data-testid={`retry-${idx}`}>
                            <RotateCw className="w-4 h-4" />
                          </button>
                        )}
                        {!uploading && (
                          <button onClick={() => removeFile(idx)} className="text-white/50 hover:text-white p-1 rounded hover:bg-white/10" data-testid={`remove-file-${idx}`}>
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {files.length > visibleFiles.length && (
                  <button type="button" onClick={() => setShowAllFiles(true)}
                    className="w-full text-xs text-white/60 hover:text-white py-2 border border-dashed border-white/10 rounded"
                    data-testid="show-all-files">
                    Show all {files.length} files ({files.length - visibleFiles.length} more)
                  </button>
                )}
                {!uploading && files.length > 1 && (
                  <div className="flex justify-end">
                    <button type="button" onClick={() => { setFiles([]); setShowAllFiles(false); }} className="text-xs text-white/50 hover:text-white" data-testid="clear-files">
                      Clear all
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Expiry */}
            {!addTo && (
            <div>
              <Label className="text-sm text-white/70">Auto-delete after</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {EXPIRY_OPTIONS.map(d => (
                  <button
                    key={d}
                    type="button"
                    disabled={uploading}
                    onClick={() => setExpiryDays(d)}
                    data-testid={`expiry-${d}`}
                    className={`px-4 py-2 rounded-md border text-sm transition
                      ${expiryDays === d ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]' : 'border-white/10 bg-white/[0.02] hover:border-white/30'}`}>
                    {d} day{d === 1 ? '' : 's'}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Upload speed preset — Slow / Balanced / Fast. Controls how many
                files upload in parallel. Only shown for Drive backends; Local
                backend uses its own sequential chunk uploader. */}
            {accountKey !== 'local' && (
              <div>
                <Label className="text-sm text-white/70">Upload speed</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="speed-preset-row">
                  {Object.entries(SPEED_PRESETS).map(([key, cfg]) => (
                    <button
                      key={key}
                      type="button"
                      disabled={uploading}
                      onClick={() => setSpeedPresetPersist(key)}
                      data-testid={`speed-${key}`}
                      className={`px-4 py-2 rounded-md border text-sm transition text-left min-w-[120px]
                        ${speedPreset === key ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]' : 'border-white/10 bg-white/[0.02] hover:border-white/30'}`}>
                      <div className="font-medium">{cfg.label}</div>
                      <div className="text-[10px] text-white/50 mt-0.5">{cfg.hint}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Optional message */}
            {!addTo && (
            <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-white/70">Title (optional)</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={uploading}
                  placeholder="e.g. Wedding raw footage" className="bg-white/5 border-white/10" data-testid="title-input" />
              </div>
              <div>
                <Label className="text-sm text-white/70">Notify (comma-separated emails)</Label>
                <Input value={recipientEmails} onChange={(e) => setRecipientEmails(e.target.value)} disabled={uploading}
                  placeholder="client@example.com" className="bg-white/5 border-white/10" data-testid="recipients-input" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-white/70">Message (optional)</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} disabled={uploading}
                rows={2} className="bg-white/5 border-white/10" data-testid="message-input" />
            </div>
            </>
            )}

            {/* Progress + actions */}
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm flex-wrap gap-2">
                  <span>Uploading…</span>
                  <div className="flex items-center gap-3 text-xs font-mono" data-testid="throughput-row">
                    {throughput.bps > 0 && (
                      <>
                        <span className="text-white/60">{formatSize(throughput.bps)}/s</span>
                        <span className="text-white/30">·</span>
                        <span className="text-white/60">{formatSize(throughput.uploadedBytes)} / {formatSize(throughput.totalBytes)}</span>
                        {throughput.etaSeconds != null && (
                          <>
                            <span className="text-white/30">·</span>
                            <span className="text-white/60" data-testid="throughput-eta">
                              ETA {throughput.etaSeconds > 3600
                                ? `${Math.floor(throughput.etaSeconds/3600)}h ${Math.round((throughput.etaSeconds%3600)/60)}m`
                                : throughput.etaSeconds > 60
                                ? `${Math.floor(throughput.etaSeconds/60)}m ${throughput.etaSeconds%60}s`
                                : `${throughput.etaSeconds}s`}
                            </span>
                          </>
                        )}
                      </>
                    )}
                    <span className="text-[#F5A623]">{overallProgress}%</span>
                  </div>
                </div>
                <Progress value={overallProgress} className="h-2" />
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={startUpload}
                disabled={uploading || files.length === 0 || !(accountKey === 'local' || selectedAccount?.connected)}
                className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90 px-6"
                data-testid="upload-btn">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading…' : `Upload ${files.length || ''} file${files.length === 1 ? '' : 's'}${files.length ? ` (${formatSize(files.reduce((a, f) => a + f.size, 0))})` : ''}${addTo ? ` to ${addTo.day.title}` : ''}`}
              </Button>
            </div>

            {/* Finalize state banner — visible when publishing the transfer */}
            {finalizeState.status === 'finalizing' && !shareResult && (
              <div className="p-4 border border-amber-500/30 bg-amber-500/5 rounded-lg flex items-center gap-3" data-testid="finalize-progress">
                <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                <div className="flex-1">
                  <div className="text-amber-300 font-medium text-sm">Publishing transfer…</div>
                  <div className="text-xs text-amber-400/70">
                    Making files shareable{finalizeState.retryCount > 0 ? ` · retry ${finalizeState.retryCount}/5` : ''}
                  </div>
                </div>
              </div>
            )}

            {finalizeState.status === 'error' && !shareResult && (
              <div className="p-4 border border-rose-500/30 bg-rose-500/5 rounded-lg space-y-3" data-testid="finalize-error">
                <div className="flex items-center gap-2 text-rose-400 font-medium">
                  <AlertCircle className="w-5 h-5" /> Couldn{"'"}t publish transfer
                </div>
                <div className="text-xs text-rose-300/80">
                  {finalizeState.error}
                </div>
                <div className="text-xs text-white/50">
                  Your files uploaded successfully — we just need to make them shareable. Click Retry.
                </div>
                <Button size="sm" onClick={retryFinalize} className="bg-rose-500 hover:bg-rose-600" data-testid="retry-finalize">
                  <RotateCw className="w-4 h-4 mr-1.5" /> Retry publish
                </Button>
              </div>
            )}

            {/* Share result — appears the moment the first session is created,
                so the photographer can copy/share the link while uploads continue
                in the background. `preparing: true` means the link exists but
                the transfer hasn't finalized yet (files still uploading). Once
                finalize completes, the panel switches to the ready state with
                the expiry date. */}
            {shareResult && (
              <div className={`p-4 border rounded-lg space-y-2 ${
                shareResult.preparing
                  ? 'border-[#F5A623]/40 bg-[#F5A623]/5'
                  : 'border-emerald-500/30 bg-emerald-500/5'
              }`} data-testid="share-result">
                <div className={`flex items-center gap-2 font-medium ${
                  shareResult.preparing ? 'text-[#F5A623]' : 'text-emerald-400'
                }`}>
                  {shareResult.preparing ? (
                    <>
                      <Clock className="w-5 h-5 animate-pulse" />
                      {addTo ? `Uploading to ${addTo.day.title} — same link as before` : 'Share link ready — copy & send now, files still uploading'}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      {shareResult.day_title ? `${shareResult.day_title} added — same link` : 'Transfer ready to share'}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Input readOnly value={shareResult.share_link} className="bg-black/40 border-white/10 font-mono text-xs" data-testid="share-link-input" />
                  <Button size="sm" variant="outline" onClick={() => copyLink(shareResult.share_link)} data-testid="copy-share-link">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                {shareResult.preparing ? (
                  <div className="text-xs text-[#F5A623]/70">
                    Anyone opening the link now will see a &quot;files being prepared&quot; screen. Once uploads finish, files appear automatically — no need to resend.
                  </div>
                ) : (
                  <div className="text-xs text-white/50">
                    {shareResult.day_title ? `${shareResult.added} file${shareResult.added === 1 ? '' : 's'} added${shareResult.face_sync === 'scheduled' ? ' · face search is indexing the new photos' : ''} · ` : ''}
                    Expires {formatDate(shareResult.expires_at)}
                  </div>
                )}
              </div>
            )}
              </>
            )}
          </CardContent>
        </Card>
        </div>

        {/* Past transfers */}
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h2 className="text-lg font-semibold">All transfers</h2>
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by title, client email, file name…"
                  className="pl-9 bg-white/5 border-white/10"
                  data-testid="search-transfers"
                />
              </div>
            </div>
            {transfers.length === 0
              ? <div className="text-sm text-white/50 py-6 text-center">{searchTerm ? 'No transfers match your search.' : 'No transfers yet.'}</div>
              : (
                <div className="space-y-2" data-testid="transfers-list">
                  {transfers.map((t) => {
                    const expanded = expandedId === t.id;
                    const recipients = t.recipient_emails || [];
                    return (
                      <div key={t.id} className="rounded-md border border-white/10 bg-white/[0.02]">
                        {/* Row */}
                        <div className="flex flex-wrap items-center gap-3 p-3">
                          <button
                            onClick={() => { setExpandedId(expanded ? null : t.id); if (!expanded) loadDays(t.id); }}
                            className="flex-1 min-w-[200px] text-left"
                            data-testid={`expand-${t.id}`}>
                            <div className="font-medium truncate flex items-center gap-2">
                              {expanded ? <ChevronUp className="w-4 h-4 shrink-0 text-white/40" /> : <ChevronDown className="w-4 h-4 shrink-0 text-white/40" />}
                              {t.title || t.drive_folder_name || '(untitled)'}
                            </div>
                            <div className="text-xs text-white/50 mt-0.5 ml-6">
                              {ACCOUNT_KEYS.find(k => k.key === t.account_key)?.label || t.account_key}
                              {' · '}{(t.files || []).length} file(s) · {formatSize(t.total_size || 0)}
                              {t.uploader_name && <> · by {t.uploader_name}</>}
                              {recipients.length > 0 && <> · sent to {recipients.length} recipient{recipients.length === 1 ? '' : 's'}</>}
                            </div>
                          </button>
                          <Badge className="bg-white/5 text-white/70 border-white/10">
                            <Clock className="w-3 h-3 mr-1" />{daysRemaining(t.expires_at)}
                          </Badge>
                          {t.share_link && (
                            <Button size="sm" variant="outline" onClick={() => copyLink(t.share_link)} data-testid={`copy-link-${t.id}`}>
                              <Copy className="w-4 h-4 mr-1" /> Copy link
                            </Button>
                          )}
                          {(t.recipient_emails || []).length > 0 && (
                            <Button size="sm" variant="outline"
                              onClick={() => copyClientGalleryLink(t)}
                              data-testid={`copy-gallery-${t.id}`}>
                              <UserRound className="w-4 h-4 mr-1" /> Client link
                            </Button>
                          )}
                          {t.share_link && daysRemaining(t.expires_at) !== 'expired' && (
                            <Button size="sm" variant="outline" onClick={() => openAddDay(t)} disabled={uploading} data-testid={`add-day-${t.id}`}>
                              <Upload className="w-4 h-4 mr-1" /> Add day
                            </Button>
                          )}
                          <FaceSearchTransferButton transferRef={t.id} onToast={(m, k) => (k === 'error' ? toast.error(m) : toast.success(m))} />
                          <Button size="sm" variant="outline" onClick={() => openResend(t)} data-testid={`resend-${t.id}`}>
                            <Send className="w-4 h-4 mr-1" /> Resend
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteDialog(t)} data-testid={`delete-transfer-${t.id}`}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>

                        {/* Expanded detail */}
                        {expanded && (
                          <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3 text-sm" data-testid={`detail-${t.id}`}>
                            {(daysByTransfer[t.id]?.days || []).length > 0 && (
                              <div className="space-y-1" data-testid={`days-${t.id}`}>
                                <div className="text-xs uppercase tracking-wide text-white/40">Days on this link</div>
                                {daysByTransfer[t.id].days.map((d) => (
                                  <div key={d.id} className="flex items-center justify-between gap-2 text-xs bg-white/[0.03] rounded px-2 py-1.5">
                                    <span className="truncate">
                                      <span className="font-medium text-white">{d.title}</span>
                                      <span className="text-white/40"> · {d.files_count} file{d.files_count === 1 ? '' : 's'} · {formatSize(d.total_size)}</span>
                                      {d.status === 'uploading' && <span className="text-[#F5A623]"> · uploading</span>}
                                    </span>
                                    <button type="button" onClick={() => renameDay(t, d)} className="text-white/50 hover:text-white shrink-0" data-testid={`rename-day-${d.id}`}>
                                      Rename
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {t.message && (
                              <div className="text-white/70 whitespace-pre-line">{t.message}</div>
                            )}
                            {t.share_link && (
                              <div className="flex items-center gap-2">
                                <LinkIcon className="w-3.5 h-3.5 text-white/40 shrink-0" />
                                <Input readOnly value={t.share_link} className="bg-black/40 border-white/10 font-mono text-xs h-8" />
                                <Button size="sm" variant="outline" onClick={() => copyLink(t.share_link)} className="h-8">
                                  <Copy className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                            {recipients.length > 0 && (
                              <div className="flex items-start gap-2 text-white/60">
                                <Mail className="w-3.5 h-3.5 mt-0.5 text-white/40 shrink-0" />
                                <div className="flex flex-wrap gap-1">
                                  {recipients.map((e) => (
                                    <span key={`recip-${e}`} className="px-2 py-0.5 rounded bg-white/5 text-xs">{e}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {(t.files || []).length > 0 && (
                              <div className="space-y-1">
                                {(t.files || []).map((f) => (
                                  <div key={f.drive_file_id} className="flex items-center justify-between text-xs bg-white/[0.03] rounded px-2 py-1.5">
                                    <span className="truncate">{f.name} <span className="text-white/40">· {formatSize(f.size)}</span></span>
                                    {f.web_content_link && (
                                      <a href={f.web_content_link} target="_blank" rel="noreferrer" className="text-[#F5A623] hover:underline ml-2 shrink-0">
                                        Download
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {t.resend_history && t.resend_history.length > 0 && (
                              <div className="text-xs text-white/50 mt-2">
                                <div className="font-medium text-white/60 mb-1">Resend history</div>
                                {t.resend_history.slice(-5).reverse().map((h, i) => (
                                  <div key={i}>
                                    {formatDate(h.at)} — {h.by_name} → {h.recipients.length} recipient{h.recipients.length === 1 ? '' : 's'} ({h.sent} sent{h.failed.length ? `, ${h.failed.length} failed` : ''})
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="text-xs text-white/40">
                              Created {formatDate(t.created_at)} · Expires {formatDate(t.expires_at)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
          </CardContent>
        </Card>

        {/* Add-day dialog — name the new day, then the composer uploads into the same link */}
        {addDayDialog && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => !addDayDialog.busy && setAddDayDialog(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <CardContent className="p-5 space-y-4" data-testid="add-day-dialog">
                <h3 className="text-lg font-semibold">Add a day to this link</h3>
                <div className="text-sm text-white/60 truncate">
                  <strong>{addDayDialog.transfer.title || addDayDialog.transfer.drive_folder_name || '(untitled)'}</strong>
                </div>
                <div className="text-xs text-white/50">
                  The new files are added to the same share link{addDayDialog.transfer.account_key !== 'local' ? ' and Drive folder' : ''}.
                  Clients see the photos grouped by day, and face search indexes the new photos automatically.
                </div>
                <div>
                  <Label className="text-sm text-white/70">Day name (optional)</Label>
                  <Input
                    value={addDayDialog.label}
                    onChange={(e) => setAddDayDialog(d => ({ ...d, label: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') confirmAddDay(); }}
                    placeholder="e.g. Haldi, Sangeet, Wedding, Reception"
                    maxLength={60}
                    className="bg-white/5 border-white/10 mt-1"
                    data-testid="add-day-label"
                  />
                  <div className="text-xs text-white/40 mt-1">
                    Will appear as “Day {(daysByTransfer[addDayDialog.transfer.id]?.days?.length || 1) + 1}{addDayDialog.label.trim() ? ` · ${addDayDialog.label.trim()}` : ''}”.
                  </div>
                </div>
                {(addDayDialog.transfer.recipient_emails || []).length > 0 && (
                  <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                    <input type="checkbox" checked={addDayDialog.notify} onChange={(e) => setAddDayDialog(d => ({ ...d, notify: e.target.checked }))} data-testid="add-day-notify" />
                    Email the {(addDayDialog.transfer.recipient_emails || []).length} recipient(s) once the day is uploaded
                  </label>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setAddDayDialog(null)} disabled={addDayDialog.busy}>Cancel</Button>
                  <Button onClick={confirmAddDay} disabled={addDayDialog.busy} className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90" data-testid="add-day-confirm">
                    <Upload className="w-4 h-4 mr-2" />{addDayDialog.busy ? 'Creating…' : 'Continue to upload'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Resend dialog */}
        {resendDialog && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => !resendBusy && setResendDialog(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <CardContent className="p-5 space-y-4" data-testid="resend-dialog">
                <h3 className="text-lg font-semibold">Resend transfer</h3>
                <div className="text-sm text-white/60">
                  <div className="truncate"><strong>{resendDialog.title || resendDialog.drive_folder_name}</strong></div>
                  {(resendDialog.recipient_emails || []).length > 0 && (
                    <div className="mt-2">Original recipients:
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(resendDialog.recipient_emails || []).map((e) =>
                          <span key={`resend-recip-${e}`} className="px-2 py-0.5 rounded bg-white/5 text-xs">{e}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-white/70">Add more recipients (optional)</Label>
                  <Input
                    value={resendExtras}
                    onChange={(e) => setResendExtras(e.target.value)}
                    placeholder="extra-client@example.com, another@example.com"
                    className="bg-white/5 border-white/10 mt-1"
                    data-testid="resend-extra-emails"
                  />
                  <div className="text-xs text-white/40 mt-1">
                    Will email {(resendDialog.recipient_emails || []).length + (resendExtras ? resendExtras.split(',').filter(s => s.trim()).length : 0)} recipient(s) with the same share link.
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setResendDialog(null)} disabled={resendBusy}>Cancel</Button>
                  <Button onClick={submitResend} disabled={resendBusy} className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90" data-testid="resend-submit">
                    <Send className="w-4 h-4 mr-2" />{resendBusy ? 'Sending…' : 'Send'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Duplicate-scan report modal (dry-run) */}
        {dupScan?.report && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setDupScan(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <CardContent className="p-5 space-y-4 overflow-y-auto" data-testid="duplicate-scan-report">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Search className="w-5 h-5 text-[#F5A623]" /> Duplicate scan report
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => setDupScan(null)} data-testid="close-dup-scan"><X className="w-4 h-4" /></Button>
                </div>
                <div className="text-xs text-white/50">
                  Dry-run · Scanned in {dupScan.report.scan_duration_seconds}s · No files were deleted.
                </div>

                {/* Grand total */}
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
                  <div className="text-xs uppercase tracking-wide text-amber-400/70 mb-1">Grand total across all accounts</div>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div><span className="text-white/60">Duplicate groups: </span><span className="font-semibold text-amber-300">{dupScan.report.grand_total.duplicate_groups_count}</span></div>
                    <div><span className="text-white/60">Extra files to delete: </span><span className="font-semibold text-amber-300">{dupScan.report.grand_total.total_duplicate_files}</span></div>
                    <div><span className="text-white/60">Recoverable storage: </span><span className="font-semibold text-amber-300">{dupScan.report.grand_total.total_wasted_human}</span></div>
                  </div>
                </div>

                {/* Per-account sections */}
                {dupScan.report.accounts.map((acct) => (
                  <div key={acct.account_key} className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid={`dup-account-${acct.account_key}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-semibold flex items-center gap-2">
                          {acct.label}
                          {acct.connected
                            ? <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">Connected</Badge>
                            : <Badge className="bg-white/5 text-white/60 border-white/10 text-[10px]">Not connected</Badge>}
                        </div>
                        {acct.connected_email && <div className="text-xs text-white/50 mt-0.5">{acct.connected_email}</div>}
                      </div>
                      {acct.summary && (
                        <div className="text-xs text-white/70 text-right">
                          <div>{acct.transfer_folders_scanned} folder(s) · {acct.total_files_scanned} file(s) scanned</div>
                          <div className="text-amber-300 font-semibold mt-0.5">
                            {acct.summary.duplicate_groups_count} group(s) · {acct.summary.total_duplicate_files} extra file(s) · {acct.summary.total_wasted_human}
                          </div>
                        </div>
                      )}
                    </div>

                    {acct.error && (
                      <div className="text-sm text-rose-400 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> {acct.error}
                      </div>
                    )}

                    {acct.duplicate_groups && acct.duplicate_groups.length === 0 && (
                      <div className="text-sm text-emerald-400 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" /> No duplicates found.
                      </div>
                    )}

                    {acct.duplicate_groups && acct.duplicate_groups.length > 0 && (
                      <div className="space-y-2">
                        {acct.duplicate_groups.slice(0, 25).map((g) => (
                          <div key={`${g.transfer_folder_id}-${g.file_name}`} className="rounded border border-white/10 bg-black/30 p-3 text-xs" data-testid={`dup-group-${g.transfer_folder_id}-${g.file_name}`}>
                            <div className="flex justify-between gap-2 flex-wrap">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-white truncate">{g.file_name}</div>
                                <div className="text-white/40 mt-0.5 truncate">In: {g.transfer_folder_name}</div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="text-amber-300 font-semibold">{g.count} copies · {formatSize(g.wasted_bytes)} wasted</div>
                                <div className="text-white/40">{formatSize(g.file_size)} each</div>
                              </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
                              <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                Keep · {g.keeper.id.slice(0, 8)}… · {g.keeper.created_time?.slice(0, 19).replace('T', ' ')}
                              </span>
                              {g.duplicates.map((d) => (
                                <span key={d.id} className="px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">
                                  Delete · {d.id.slice(0, 8)}… · {d.created_time?.slice(0, 19).replace('T', ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                        {acct.duplicate_groups.length > 25 && (
                          <div className="text-xs text-white/50 text-center py-2">
                            + {acct.duplicate_groups.length - 25} more group(s) — export or paginate to see all
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
                  <Button variant="outline" onClick={() => {
                    const blob = new Blob([JSON.stringify(dupScan.report, null, 2)], { type: 'application/json' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `duplicate-scan-${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.json`;
                    a.click();
                    URL.revokeObjectURL(a.href);
                  }} data-testid="download-dup-report">
                    Download JSON report
                  </Button>
                  <Button onClick={() => setDupScan(null)} className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90">Close</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {dupScan?.error && !dupScan.report && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setDupScan(null)}>
            <Card className="bg-[#121212] border-rose-500/30 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <CardContent className="p-5 space-y-3" data-testid="duplicate-scan-error">
                <div className="flex items-center gap-2 text-rose-400 font-medium">
                  <AlertCircle className="w-5 h-5" /> Scan failed
                </div>
                <div className="text-sm text-rose-300/80">{dupScan.error}</div>
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setDupScan(null)}>Close</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete-confirmation modal — replaces window.confirm so users see
            exactly how many Drive files will be permanently deleted. */}
        {deleteDialog && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => !deleteBusy && setDeleteDialog(null)}>
            <Card className="bg-[#121212] border-rose-500/30 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <CardContent className="p-5 space-y-4" data-testid="delete-transfer-dialog">
                <div className="flex items-center gap-2 text-rose-400 font-semibold">
                  <AlertCircle className="w-5 h-5" /> Permanently delete this transfer?
                </div>
                <div className="text-sm text-white/80 space-y-2">
                  <div className="truncate">
                    <strong>{deleteDialog.title || deleteDialog.drive_folder_name || '(untitled)'}</strong>
                  </div>
                  <div className="text-rose-300">
                    This will permanently delete <strong data-testid="delete-file-count">{(deleteDialog.files || []).length}</strong> file{(deleteDialog.files || []).length === 1 ? '' : 's'}
                    {deleteDialog.account_key === 'local' ? ' from local storage' : ' from Google Drive'}
                    {deleteDialog.total_size ? ` (${formatSize(deleteDialog.total_size)})` : ''}. Continue?
                  </div>
                  <div className="text-xs text-white/50">
                    The share link will stop working immediately and the files cannot be recovered.
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setDeleteDialog(null)} disabled={deleteBusy} data-testid="cancel-delete">
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      setDeleteBusy(true);
                      try { await handleDelete(deleteDialog.id); }
                      finally { setDeleteBusy(false); }
                    }}
                    disabled={deleteBusy}
                    className="bg-rose-500 hover:bg-rose-600 text-white"
                    data-testid="confirm-delete">
                    <Trash2 className="w-4 h-4 mr-2" />
                    {deleteBusy ? 'Deleting…' : 'Delete permanently'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Client-gallery email picker — only shown when a transfer has multiple
            recipients and the admin needs to choose which client link to copy. */}
        {galleryPicker && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setGalleryPicker(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <CardContent className="p-5 space-y-4" data-testid="gallery-picker-dialog">
                <div className="flex items-center gap-2 text-white font-semibold">
                  <UserRound className="w-5 h-5 text-[#F5A623]" /> Copy client gallery link
                </div>
                <div className="text-sm text-white/60">
                  This transfer was sent to multiple people. Which client&apos;s persistent gallery link do you want to copy?
                </div>
                <div className="space-y-1.5">
                  {galleryPicker.emails.map((e) => (
                    <button key={`pick-${e}`}
                      onClick={() => copyGalleryFor(galleryPicker.transfer.account_key, e)}
                      className="w-full text-left px-3 py-2 rounded border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#F5A623]/40 text-sm truncate"
                      data-testid={`pick-gallery-${e}`}>
                      {e}
                    </button>
                  ))}
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setGalleryPicker(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransfersPage;
